import React, { useEffect, useState } from 'react';
import FilterPanel from '../../components/ui/FilterPanel';
import Table from '../../components/ui/Table';
import { FileTextIcon, UserIcon, AlertCircleIcon, CheckCircleIcon, RefreshCwIcon } from 'lucide-react';
// Generate mock audit log data
const generateAuditLogs = (count = 100) => {
  const actions = ['User Login', 'User Logout', 'User Created', 'User Updated', 'User Deleted', 'Role Created', 'Role Updated', 'Role Deleted', 'Permission Changed', 'Merchant Created', 'Merchant Updated', 'Transaction Approved', 'Transaction Rejected', 'Report Exported', 'Settings Changed', 'Password Changed', 'Failed Login Attempt'];
  const users = ['ahmed.khan', 'fatima.ali', 'muhammad.zubair', 'ayesha.malik', 'imran.hussain', 'zainab.iqbal', 'farhan.ahmed', 'sana.sheikh', 'ali.raza', 'saima.khan', 'bilal.siddiqui', 'rabia.zahid', 'system'];
  const ipAddresses = ['192.168.1.1', '10.0.0.1', '172.16.0.1', '127.0.0.1', '192.168.0.100'];
  const statuses = ['Success', 'Failed', 'Warning'];
  const logs = [];
  const now = new Date();
  const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
  for (let i = 0; i < count; i++) {
    const action = actions[Math.floor(Math.random() * actions.length)];
    const status = statuses[Math.floor(Math.random() * statuses.length)];
    const timestamp = new Date(thirtyDaysAgo.getTime() + Math.random() * (now.getTime() - thirtyDaysAgo.getTime()));
    logs.push({
      id: i + 1,
      timestamp: timestamp.toISOString(),
      user: users[Math.floor(Math.random() * users.length)],
      action,
      details: `Details for ${action.toLowerCase()}`,
      ipAddress: ipAddresses[Math.floor(Math.random() * ipAddresses.length)],
      status
    });
  }
  // Sort by timestamp (newest first)
  return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
};
const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<any[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<any[]>([]);
  // Filter states
  const [userFilter, setUserFilter] = useState<string>('All');
  const [actionFilter, setActionFilter] = useState<string>('All');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [fromDate, setFromDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 7);
    return date.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState<string>(() => new Date().toISOString().split('T')[0]);
  // Get unique users and actions for filters
  const uniqueUsers = ['All', ...new Set(logs.map(log => log.user))];
  const uniqueActions = ['All', ...new Set(logs.map(log => log.action))];
  const statusOptions = ['All', 'Success', 'Failed', 'Warning'];
  useEffect(() => {
    // Load mock data
    const auditLogs = generateAuditLogs(100);
    setLogs(auditLogs);
    setFilteredLogs(auditLogs);
  }, []);
  const applyFilters = () => {
    let filtered = [...logs];
    // Filter by user
    if (userFilter !== 'All') {
      filtered = filtered.filter(log => log.user === userFilter);
    }
    // Filter by action
    if (actionFilter !== 'All') {
      filtered = filtered.filter(log => log.action === actionFilter);
    }
    // Filter by status
    if (statusFilter !== 'All') {
      filtered = filtered.filter(log => log.status === statusFilter);
    }
    // Filter by date range
    const fromDateObj = new Date(fromDate);
    const toDateObj = new Date(toDate);
    toDateObj.setHours(23, 59, 59, 999); // End of the day
    filtered = filtered.filter(log => {
      const logDate = new Date(log.timestamp);
      return logDate >= fromDateObj && logDate <= toDateObj;
    });
    setFilteredLogs(filtered);
  };
  const resetFilters = () => {
    setUserFilter('All');
    setActionFilter('All');
    setStatusFilter('All');
    const date = new Date();
    setFromDate(new Date(date.setDate(date.getDate() - 7)).toISOString().split('T')[0]);
    setToDate(new Date().toISOString().split('T')[0]);
    setFilteredLogs(logs);
  };
  const handleExport = (type: 'xlsx' | 'csv' | 'pdf') => {
    alert(`Exporting audit logs as ${type.toUpperCase()}`);
  };
  // Table columns
  const columns = [{
    key: 'timestamp',
    header: 'Timestamp'
  }, {
    key: 'user',
    header: 'User'
  }, {
    key: 'action',
    header: 'Action'
  }, {
    key: 'details',
    header: 'Details'
  }, {
    key: 'ipAddress',
    header: 'IP Address'
  }, {
    key: 'status',
    header: 'Status'
  }];
  // Format data for table
  const formattedLogs = filteredLogs.map(log => ({
    ...log,
    timestamp: new Date(log.timestamp).toLocaleString(),
    status: <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
        ${log.status === 'Success' ? 'bg-green-100 text-green-800' : log.status === 'Failed' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-620">
        {log.status}
      </span>
  }));
  return <div className="space-y-6" data-id="element-621">
      <div className="flex items-center justify-between" data-id="element-622">
        <h1 className="text-2xl font-semibold text-gray-800" data-id="element-623">Audit Logs</h1>
        <button onClick={() => window.location.reload()} className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-624">
          <RefreshCwIcon className="h-4 w-4 mr-1" data-id="element-625" />
          Refresh
        </button>
      </div>
      <FilterPanel title="Filter Audit Logs" onApplyFilters={applyFilters} onResetFilters={resetFilters} data-id="element-626">
        <div data-id="element-627">
          <label htmlFor="user" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-628">
            User
          </label>
          <select id="user" value={userFilter} onChange={e => setUserFilter(e.target.value)} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-629">
            {uniqueUsers.map(user => <option key={user} value={user} data-id="element-630">
                {user}
              </option>)}
          </select>
        </div>
        <div data-id="element-631">
          <label htmlFor="action" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-632">
            Action
          </label>
          <select id="action" value={actionFilter} onChange={e => setActionFilter(e.target.value)} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-633">
            {uniqueActions.map(action => <option key={action} value={action} data-id="element-634">
                {action}
              </option>)}
          </select>
        </div>
        <div data-id="element-635">
          <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-636">
            Status
          </label>
          <select id="status" value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-637">
            {statusOptions.map(status => <option key={status} value={status} data-id="element-638">
                {status}
              </option>)}
          </select>
        </div>
        <div data-id="element-639">
          <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-640">
            From Date
          </label>
          <input type="date" id="fromDate" value={fromDate} onChange={e => setFromDate(e.target.value)} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-641" />
        </div>
        <div data-id="element-642">
          <label htmlFor="toDate" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-643">
            To Date
          </label>
          <input type="date" id="toDate" value={toDate} onChange={e => setToDate(e.target.value)} className="block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-644" />
        </div>
      </FilterPanel>
      <div className="bg-white rounded-lg shadow overflow-hidden" data-id="element-645">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center" data-id="element-646">
          <FileTextIcon className="h-5 w-5 text-gray-400 mr-2" data-id="element-647" />
          <h2 className="text-lg font-medium text-gray-800" data-id="element-648">
            Audit Log Records
          </h2>
          <span className="ml-2 px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800" data-id="element-649">
            {filteredLogs.length} records
          </span>
        </div>
        <Table columns={columns} data={formattedLogs} onExport={handleExport} data-id="element-650" />
      </div>
    </div>;
};
export default AuditLogs;